import { useMemo, useRef, useState } from 'react';

import { cn } from '@rocco/utils/cn';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import { getLinkedProfileLocations } from '@modules/professional/util/contact-page-helper';
import { LocationMap } from '@modules/professional/component/contact-tab/LocationMap';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';
import type {
    EnquiryProfessional,
    UseEnquiryProps,
} from '~/modules/enquiry/hooks/use-enquiry';
import { GetInTouchEnquiryForm } from './GetInTouchEnquiryForm';
import type { EnquiryFunnelStage } from './use-enquiry-funnel-stages';

/** Everything the row needs of a professional, derived from its consumers so
 *  callers can pass a loader's professional plus `branchOptions`. */
export type GetInTouchProfessional = EnquiryProfessional &
    Parameters<typeof getLinkedProfileLocations>[0] &
    Parameters<typeof useEnquiryVisibility>[0] & {
        Country?: string | null;
    };

export interface GetInTouchRowContentProps {
    /** Professional the enquiry goes to; also supplies the map locations. */
    professional: GetInTouchProfessional;
    /** Kind of page the enquiry is sent from — the enquiry is recorded and
     *  tracked against this, so it must match the host page. */
    itemType: UseEnquiryProps['itemType'];
    /** The page being enquired about. */
    siteTreeID: number;
    /** Title of the page being enquired about (used for tracking). */
    itemTitle: string;
    /** Project-only tracking details; ignored for other item types. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** Fills "…about {itemDescription}" in the specifier prompt, e.g. "this project". */
    itemDescription: string;
    /**
     * Enquiry funnel stages already fired this page view. Owned by the caller
     * because this row lives inside an accordion that unmounts it when closed
     * — create it with useRef in a component that stays mounted for the whole
     * page view, so reopening the row doesn't refire the funnel but a fresh
     * page view (including browser back/forward) does.
     */
    firedFunnelStages: Set<EnquiryFunnelStage>;
    /**
     * `dark` for a dark accordion section (project), `light` for ink-on-white
     * editorial surfaces (article). Only the surrounding type changes — the
     * enquiry form itself is a dark card on both, per the design.
     */
    theme?: 'dark' | 'light';
}

/**
 * Inline "Get in touch" experience for a detail page's numbered accordion:
 * the professional's location map with a primary-office card on the left, and
 * the inline enquiry form on the right. Reuses the shared booking/enquiry
 * backend, so there is no popup modal here.
 */
export const GetInTouchRowContent = ({
    professional,
    itemType,
    siteTreeID,
    itemTitle,
    relatedItem,
    itemDescription,
    firedFunnelStages,
    theme = 'dark',
}: GetInTouchRowContentProps) => {
    const { showEnquiryForm, showSpecifierLoginPrompt } =
        useEnquiryVisibility(professional);

    const listRef = useRef<HTMLDivElement>(null);

    // Only the professional's own locations (no linked profiles here).
    const locations = useMemo(
        () => getLinkedProfileLocations(professional, null),
        [professional],
    );

    const {
        mapCenter,
        mapZoomNum,
        onMapMarkerHover,
        onMapMarkerClick,
        onLocationDetailClose,
        hoveredLocation,
        clickedLocation,
        filteredLocations,
        sortedLocations,
    } = useContactLocationMap(
        locations,
        listRef,
        true,
        professional.Country ?? undefined,
        // Open zoomed out so all branches show; zoom in only on pin click.
        false,
    );

    const [showPhone, setShowPhone] = useState(false);
    // The card follows the clicked pin, falling back to the head office (first
    // location) before any pin is selected.
    const active = clickedLocation ?? sortedLocations[0];
    const headOffice = active
        ? [active.Suburb, active.City].filter(Boolean).join(', ')
        : '';

    return (
        <div
            className={cn(
                'pb-[9.6rem] md:pt-8',
                theme === 'dark' ? 'text-white' : 'text-[#1f1f21]',
            )}
        >
            <div className="flex max-md:flex-col max-md:gap-8 md:gap-[8.2%]">
                {/* Map + slim primary-location card. */}
                <div
                    className={cn(
                        'relative min-w-0 overflow-hidden rounded-[0.5rem] max-md:h-[24rem] md:h-[42.8125rem] md:w-[57.9%]',
                        theme === 'dark' ? 'bg-neutral-800' : 'bg-[#f1f1f1]',
                    )}
                >
                    {sortedLocations.length > 0 ? (
                        <LocationMap
                            center={mapCenter}
                            zoom={mapZoomNum}
                            filteredLocations={filteredLocations}
                            onMapMarkerHover={onMapMarkerHover}
                            onMapMarkerClick={onMapMarkerClick}
                            onMapPopupClose={onLocationDetailClose}
                            hoveredLocation={hoveredLocation}
                            clickedLocation={clickedLocation}
                            linkedProfiles={undefined}
                            professionalCountry={
                                professional.Country ?? undefined
                            }
                        />
                    ) : null}

                    {active ? (
                        <div className="flex flex-col gap-[1.25rem] rounded-[0.5rem] bg-[#fafafa] p-[1.5rem] text-[#141414] shadow-[0px_4px_14px_0px_rgba(0,0,0,0.1)] max-md:mt-4 md:absolute md:top-[1.75rem] md:left-[1.625rem] md:w-[27.375rem]">
                            <div>
                                <p className="m-0 truncate font-sans leading-[1.6] text-[#141414] max-md:text-lg md:text-[1.375rem] md:tracking-[-0.041rem]">
                                    {professional.Title}
                                </p>
                                {headOffice ? (
                                    <p className="m-0 truncate font-sans leading-[1.6] text-[#aeaeae] max-md:text-sm md:text-base md:tracking-[-0.03rem]">
                                        Head Office: {headOffice}
                                    </p>
                                ) : null}
                            </div>
                            <div className="h-px w-full bg-black/10" />
                            <div className="flex flex-col gap-1 text-sm text-[#141414]">
                                {active.Address1 ? (
                                    <p className="m-0 font-sans leading-[1.43]">
                                        {active.Address1}
                                    </p>
                                ) : null}
                                {active.Phone ? (
                                    showPhone ? (
                                        <a
                                            href={`tel:${active.Phone}`}
                                            className="font-sans leading-[1.43] underline"
                                        >
                                            {active.Phone}
                                        </a>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setShowPhone(true)}
                                            className="w-fit font-sans leading-[1.43] underline"
                                        >
                                            Show Phone number
                                        </button>
                                    )
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Inline enquiry form (dark-themed, scoped). */}
                <div className="rounded-[0.75rem] bg-[#2d2d2d] max-md:w-full max-md:p-6 md:w-[28.55%] md:self-stretch md:p-[2.5rem]">
                    {showSpecifierLoginPrompt ? (
                        <SpecifierLoginPrompt
                            professionalName={professional.Title}
                            itemDescription={itemDescription}
                            showBottomBorder={false}
                            className="[&_*]:text-white!"
                        />
                    ) : showEnquiryForm ? (
                        <GetInTouchEnquiryForm
                            professional={professional}
                            itemType={itemType}
                            siteTreeID={siteTreeID}
                            itemTitle={itemTitle}
                            relatedItem={relatedItem}
                            firedFunnelStages={firedFunnelStages}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
